import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import IsetLogo from "../../assets/img/clients/Iset.png";

// Adjust this path to wherever analytics.js actually lives
import { trackCaseStudyView, trackWhatsAppClick, trackEvent } from "../../utils/analytics";

/* =========================================================
   FEATURES
========================================================= */

const features = [
  {
    title: "Balloon Satellite",
    text: "A custom-built balloon satellite system designed to collect environmental and positional data during high-altitude flight.",
  },
  {
    title: "Real-time Sensor Data",
    text: "The satellite continuously collects live environmental and positional data from multiple onboard sensors.",
  },
  {
    title: "Temperature Monitoring",
    text: "DHT22 is used to capture temperature readings and transmit telemetry data to the monitoring platform.",
  },
  {
    title: "Humidity Monitoring",
    text: "Humidity information is collected through the DHT22 sensor and made available through the real-time telemetry dashboard.",
  },
  {
    title: "Air Pressure Monitoring",
    text: "BMP sensor data is captured to monitor atmospheric pressure during the balloon's flight.",
  },
  {
    title: "GPS Location Tracking",
    text: "NEO-6M GPS provides geographical coordinates and location information for the balloon satellite.",
  },
  {
    title: "Speed Monitoring",
    text: "GPS telemetry is used to determine and display the movement speed of the balloon satellite.",
  },
  {
    title: "UV Monitoring",
    text: "UV telemetry is displayed as part of the environmental information available through the monitoring system.",
  },
  {
    title: "LoRa Communication",
    text: "LoRa technology is used for long-range wireless transmission of telemetry data from the satellite system.",
  },
  {
    title: "API-based Data Transfer",
    text: "Telemetry received from the satellite is transferred through APIs to the centralized monitoring dashboard.",
  },
  {
    title: "Real-time Dashboard",
    text: "A dedicated dashboard displays live telemetry including location, temperature, humidity, pressure, speed and UV information.",
  },
  {
    title: "Custom PCB",
    text: "A custom-designed PCB integrates the electronic components and provides a compact, structured hardware platform.",
  },
];

/* =========================================================
   SCREENSHOT TITLES
========================================================= */

const screenshotTitles = {
  "Ballon Path.JPEG": "Balloon Flight Path",
  "Limca Book of record Certificate.JPEG":
    "Limca Book of Records Certificate",
  "India and Limca Book of Records Medals.JPEG":
    "India & Limca Book of Records Medals",
  "India Book of Record Certificate.JPEG":
    "India Book of Records Certificate",
  "Participation Certificate.JPEG":
    "Participation Certificate",
};

/* =========================================================
   PROJECT IMAGES
========================================================= */

const SatelliteImages = require.context(
  "../../assets/img/ISET",
  false,
  /\.(png|jpe?g|webp|gif)$/i
);

const screenshotData = SatelliteImages.keys().map((file) => {
  const fileName = file.replace("./", "");

  return {
    fileName,
    image: SatelliteImages(file),
  };
});

/* =========================================================
   WORKFLOW
========================================================= */

const workflow = [
  {
    number: "01",
    title: "Sensor Data Collection",
    text: "Multiple onboard sensors continuously collect environmental and positional information.",
  },
  {
    number: "02",
    title: "ESP32 Processing",
    text: "ESP32 acts as the central controller, reading and processing telemetry from connected sensors and modules.",
  },
  {
    number: "03",
    title: "Telemetry Transmission",
    text: "Processed telemetry data is transmitted over a long-range LoRa communication link.",
  },
  {
    number: "04",
    title: "Data Reception",
    text: "The transmitted telemetry is received by the ground-side communication system.",
  },
  {
    number: "05",
    title: "API Integration",
    text: "The received information is transferred to the software platform through APIs.",
  },
  {
    number: "06",
    title: "Real-time Dashboard",
    text: "Live telemetry is displayed on a centralized dashboard for monitoring and analysis.",
  },
];

/* =========================================================
   TECHNOLOGIES
========================================================= */

const technologies = [
  "ESP32",
  "DHT22",
  "BMP Sensor",
  "NEO-6M GPS",
  "LoRa",
  "Custom PCB",
  "REST API",
  "Real-time Dashboard",
];

/* =========================================================
   TELEMETRY
========================================================= */

const telemetry = [
  {
    title: "Real-time Location",
    text: "GPS coordinates provide the current geographical position of the balloon satellite.",
  },
  {
    title: "Temperature",
    text: "DHT22 provides temperature telemetry collected during the balloon flight.",
  },
  {
    title: "Humidity",
    text: "Humidity readings are collected through the onboard environmental sensor.",
  },
  {
    title: "Air Pressure",
    text: "BMP sensor data provides atmospheric pressure information.",
  },
  {
    title: "GPS Speed",
    text: "GPS telemetry is used to monitor the movement speed of the satellite platform.",
  },
  {
    title: "UV Information",
    text: "UV information is presented as part of the environmental telemetry available through the system.",
  },
];

/* =========================================================
   IMPACT
========================================================= */

const impact = [
  "Enabled real-time monitoring of balloon satellite telemetry",
  "Integrated multiple sensors into a single embedded system",
  "Enabled long-range telemetry communication using LoRa",
  "Connected hardware telemetry with a software monitoring dashboard",
  "Provided real-time geographical location tracking",
  "Enabled monitoring of environmental parameters during flight",
  "Created a centralized real-time telemetry dashboard",
  "Demonstrated practical implementation of IoT and space technology",
  "Integrated custom electronics using a purpose-built PCB",
  "Created a technology platform suitable for educational and research demonstrations",
];

/* =========================================================
   COMPONENT
========================================================= */

export default function Satellites() {
  /* =========================================================
     SEO META + STRUCTURED DATA
  ========================================================= */

  useEffect(() => {
    document.title =
      "Balloon Satellite & Real-Time Telemetry System | Indo Science Education Trust | HexxaTech";

    // GA4: record a case-study-view event for this page
    trackCaseStudyView("ISET Balloon Satellite");

    const metaTags = [
      {
        name: "description",
        content:
          "Explore the balloon satellite and real-time telemetry system developed by HexxaTech for Indo Science Education Trust, using ESP32, DHT22, BMP, NEO-6M GPS, LoRa, custom PCB, APIs and a live monitoring dashboard.",
      },
      {
        name: "keywords",
        content:
          "balloon satellite, balloon satellite project, satellite telemetry system, real time satellite telemetry, LoRa telemetry, ESP32 satellite, DHT22 satellite, BMP sensor, NEO-6M GPS, real time satellite dashboard, IoT space technology, embedded systems, custom PCB, satellite monitoring dashboard, Indo Science Education Trust, ISET, ISRO Space Tutor, space technology India, HexxaTech Softwares",
      },
      {
        name: "robots",
        content:
          "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "author",
        content: "HexxaTech Softwares",
      },
      {
        name: "publisher",
        content: "HexxaTech Softwares",
      },
    ];

    metaTags.forEach(({ name, content }) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    });

    const linkTags = [
      {
        rel: "canonical",
        href: "https://hexxatech.co.in/case-studies/indo-science",
      },
    ];

    linkTags.forEach(({ rel, href }) => {
      let element = document.querySelector(`link[rel="${rel}"]`);

      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }

      element.setAttribute("href", href);
    });

    /* =========================================================
       OPEN GRAPH
    ========================================================= */

    const ogTags = [
      {
        property: "og:title",
        content:
          "Balloon Satellite & Real-Time Telemetry System | Indo Science Education Trust",
      },
      {
        property: "og:description",
        content:
          "A balloon satellite telemetry system developed by HexxaTech using ESP32, sensors, LoRa communication, APIs and a real-time monitoring dashboard.",
      },
      {
        property: "og:url",
        content:
          "https://hexxatech.co.in/case-studies/indo-science",
      },
      {
        property: "og:type",
        content: "article",
      },
      {
        property: "og:site_name",
        content: "HexxaTech Softwares",
      },
    ];

    ogTags.forEach(({ property, content }) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    });

    /* =========================================================
       TWITTER CARD
    ========================================================= */

    const twitterTags = [
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content:
          "Balloon Satellite & Real-Time Telemetry System",
      },
      {
        name: "twitter:description",
        content:
          "Real-time balloon satellite telemetry system developed for Indo Science Education Trust using ESP32, LoRa, sensors, APIs and a live dashboard.",
      },
    ];

    twitterTags.forEach(({ name, content }) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    });

    /* =========================================================
       JSON-LD STRUCTURED DATA
    ========================================================= */

    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id":
            "https://hexxatech.co.in/case-studies/indo-science#article",
          headline:
            "Balloon Satellite & Real-Time Telemetry System",
          description:
            "A balloon satellite telemetry and real-time monitoring system developed for Indo Science Education Trust using ESP32, sensors, LoRa communication, APIs and a custom dashboard.",
          author: {
            "@type": "Organization",
            name: "HexxaTech Softwares",
            url: "https://hexxatech.co.in/",
          },
          publisher: {
            "@type": "Organization",
            name: "HexxaTech Softwares",
            url: "https://hexxatech.co.in/",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://hexxatech.co.in/case-studies/indo-science",
          },
          about: {
            "@type": "Thing",
            name: "Balloon Satellite Telemetry System",
          },
          keywords:
            "balloon satellite, satellite telemetry, LoRa, ESP32, IoT, real-time dashboard, space technology",
        },

        {
          "@type": "Organization",
          "@id": "https://indoscience.org/#organization",
          name: "Indo Science Education Trust",
          alternateName: "ISET",
          url: "https://indoscience.org/",
        },

        {
          "@type": "BreadcrumbList",
          "@id":
            "https://hexxatech.co.in/case-studies/indo-science#breadcrumb",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://hexxatech.co.in/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Case Studies",
              item: "https://hexxatech.co.in/case-studies",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Indo Science Education Trust",
              item:
                "https://hexxatech.co.in/case-studies/indo-science",
            },
          ],
        },
      ],
    };

    let jsonLd = document.getElementById(
      "indo-science-case-study-schema"
    );

    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.type = "application/ld+json";
      jsonLd.id = "indo-science-case-study-schema";
      document.head.appendChild(jsonLd);
    }

    jsonLd.textContent = JSON.stringify(structuredData);

    return () => {
      const schema = document.getElementById(
        "indo-science-case-study-schema"
      );

      if (schema) {
        schema.remove();
      }
    };
  }, []);

  return (
    <>
      <style>{`

        * {
          box-sizing: border-box;
        }

        /* =========================================================
           PAGE
        ========================================================= */

        .cs-page {
          min-height: 100vh;

          background:
            radial-gradient(
              circle at 10% 0%,
              rgba(37, 99, 235, 0.08),
              transparent 28%
            ),
            radial-gradient(
              circle at 90% 15%,
              rgba(99, 102, 241, 0.07),
              transparent 25%
            ),
            #ffffff;

          color: #111827;

          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .cs-container {
          max-width: 1240px;
          margin: 0 auto;
          padding: 60px 24px 80px;
        }

        /* =========================================================
           BREADCRUMB
        ========================================================= */

        .cs-breadcrumb {
          display: inline-flex;
          align-items: center;
          gap: 8px;

          color: #64748b;

          font-size: 14px;
          font-weight: 500;

          text-decoration: none;

          transition: color 0.2s ease;
        }

        .cs-breadcrumb:hover {
          color: #2563eb;
        }

        /* =========================================================
           HERO
        ========================================================= */

        .cs-hero {
          max-width: 980px;
          margin: 45px auto 0;
          text-align: center;
        }

        .cs-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;

          padding: 8px 14px;

          border: 1px solid rgba(37, 99, 235, 0.15);
          border-radius: 999px;

          background: rgba(37, 99, 235, 0.06);

          color: #2563eb;

          font-size: 13px;
          font-weight: 700;

          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .cs-eyebrow-dot {
          width: 7px;
          height: 7px;

          border-radius: 50%;

          background: #2563eb;
        }

        /* =========================================================
           CLIENT LOGO
        ========================================================= */

        .cs-logo {
          width: 150px;
          height: 90px;

          margin: 28px auto 20px;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 12px 20px;

          background: #ffffff;

          border: 1px solid #e2e8f0;

          border-radius: 18px;

          box-shadow:
            0 12px 35px rgba(15, 23, 42, 0.06);
        }

        .cs-logo img {
          max-width: 100%;
          max-height: 65px;

          width: auto;
          height: auto;

          object-fit: contain;

          display: block;
        }

        .cs-hero h1 {
          margin: 20px 0 14px;

          font-size: clamp(42px, 6vw, 64px);

          line-height: 1.05;

          letter-spacing: -0.045em;

          font-weight: 800;

          color: #0f172a;
        }

        .cs-gradient-text {
          background:
            linear-gradient(
              90deg,
              #2563eb,
              #4f46e5,
              #7c3aed
            );

          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;

          background-clip: text;
        }

        .cs-subtitle {
          font-size: clamp(20px, 2.4vw, 26px);

          font-weight: 700;

          margin: 0 0 20px;

          color: #1e293b;
        }

        .cs-hero-description {
          max-width: 850px;

          margin: 0 auto;

          color: #64748b;

          font-size: 18px;

          line-height: 1.8;
        }

        .cs-trust {
          display: flex;

          justify-content: center;

          align-items: center;

          gap: 22px;

          margin-top: 28px;

          color: #94a3b8;

          font-size: 13px;

          flex-wrap: wrap;
        }

        .cs-trust-item {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .cs-trust-dot {
          width: 5px;
          height: 5px;

          border-radius: 50%;

          background: #94a3b8;
        }

        /* =========================================================
           OFFICIAL LINKS
        ========================================================= */

        .cs-official-links {
          display: flex;

          justify-content: center;

          gap: 12px;

          flex-wrap: wrap;

          margin-top: 24px;
        }

        .cs-official-link {
          display: inline-flex;

          align-items: center;

          gap: 8px;

          padding: 9px 14px;

          border-radius: 10px;

          border: 1px solid #e2e8f0;

          background: #ffffff;

          color: #334155;

          font-size: 13px;

          font-weight: 600;

          text-decoration: none;

          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .cs-official-link:hover {
          transform: translateY(-2px);

          border-color: #bfdbfe;

          box-shadow:
            0 10px 25px rgba(15, 23, 42, 0.06);

          color: #2563eb;
        }

        /* =========================================================
           PROJECT INFO
        ========================================================= */

        .cs-project-info {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 18px;

          margin-top: 60px;
        }

        .cs-project-info-card {
          padding: 24px;

          border: 1px solid #e2e8f0;

          border-radius: 18px;

          background: rgba(255, 255, 255, 0.85);

          box-shadow:
            0 12px 35px rgba(15, 23, 42, 0.05);
        }

        .cs-project-info-label {
          font-size: 11px;

          font-weight: 800;

          letter-spacing: 0.1em;

          text-transform: uppercase;

          color: #2563eb;

          margin-bottom: 8px;
        }

        .cs-project-info-value {
          font-size: 15px;

          font-weight: 600;

          color: #334155;

          line-height: 1.5;
        }

        /* =========================================================
           SECTION HEADER
        ========================================================= */

        .cs-section-header {
          margin: 95px 0 34px;
          padding: 0 !important;
        }

        .cs-section-label {
          color: #2563eb;

          font-size: 13px;

          font-weight: 700;

          letter-spacing: 0.1em;

          text-transform: uppercase;
        }

        .cs-section-header h2 {
          margin: 10px 0 0;

          font-size: clamp(28px, 3.6vw, 40px);

          letter-spacing: -0.04em;

          line-height: 1.15;

          color: #0f172a;
        }

        /* =========================================================
           SPLIT PANELS
        ========================================================= */

        .cs-split {
          display: grid;

          grid-template-columns: repeat(2, 1fr);

          gap: 28px;

          padding-top: 10px;
        }

        .cs-panel {
          border: 1px solid #e2e8f0;

          border-radius: 24px;

          background: rgba(255, 255, 255, 0.85);

          box-shadow:
            0 20px 60px rgba(15, 23, 42, 0.06);

          padding: 36px;

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-panel:hover {
          transform: translateY(-4px);

          box-shadow:
            0 25px 70px rgba(15, 23, 42, 0.09);
        }

        .cs-panel-label {
          font-size: 13px;

          font-weight: 700;

          letter-spacing: 0.08em;

          text-transform: uppercase;

          margin-bottom: 14px;
        }

        .cs-panel p {
          color: #475569;

          line-height: 1.8;

          font-size: 15.5px;

          margin: 0;
        }

        /* =========================================================
           CREDIBILITY / RECOGNITION
        ========================================================= */

        .cs-credibility {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 18px;

          padding-top: 15px;
        }

        .cs-credibility-card {
          padding: 28px;

          border: 1px solid #e2e8f0;

          border-radius: 20px;

          background: #ffffff;

          box-shadow:
            0 15px 40px rgba(15, 23, 42, 0.05);

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-credibility-card:hover {
          transform: translateY(-5px);

          box-shadow:
            0 25px 55px rgba(15, 23, 42, 0.09);
        }

        .cs-credibility-icon {
          width: 46px;
          height: 46px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 18px;

          border-radius: 14px;

          background:
            linear-gradient(
              135deg,
              rgba(37, 99, 235, 0.12),
              rgba(124, 58, 237, 0.10)
            );

          color: #2563eb;

          font-size: 15px;

          font-weight: 800;
        }

        .cs-credibility-card h3 {
          margin: 0 0 9px;

          color: #0f172a;

          font-size: 17px;
        }

        .cs-credibility-card p {
          margin: 0;

          color: #64748b;

          font-size: 14px;

          line-height: 1.7;
        }

        .cs-credibility-card a {
          display: inline-block;

          margin-top: 14px;

          color: #2563eb;

          font-size: 13px;

          font-weight: 700;

          text-decoration: none;
        }

        .cs-credibility-card a:hover {
          text-decoration: underline;
        }

        /* =========================================================
           ARCHITECTURE
        ========================================================= */

        .cs-architecture {
          margin-top: 25px;

          padding: 36px;

          border: 1px solid #e2e8f0;

          border-radius: 24px;

          background: #ffffff;

          box-shadow:
            0 20px 55px rgba(15, 23, 42, 0.06);
        }

        .cs-architecture-flow {
          display: flex;

          align-items: center;

          justify-content: center;

          flex-wrap: wrap;

          gap: 12px;
        }

        .cs-architecture-node {
          padding: 16px 20px;

          border-radius: 14px;

          border: 1px solid #dbeafe;

          background: #f8fbff;

          color: #1e3a8a;

          font-size: 14px;

          font-weight: 700;

          text-align: center;
        }

        .cs-architecture-arrow {
          color: #94a3b8;

          font-size: 20px;

          font-weight: 700;
        }

        .cs-architecture-description {
          max-width: 850px;

          margin: 25px auto 0;

          text-align: center;

          color: #64748b;

          font-size: 14px;

          line-height: 1.7;
        }

        /* =========================================================
           WORKFLOW
        ========================================================= */

        .cs-workflow {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 20px;

          padding-top: 15px;
        }

        .cs-workflow-card {
          position: relative;

          padding: 28px;

          border-radius: 20px;

          border: 1px solid #e2e8f0;

          background: #ffffff;

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-workflow-card:hover {
          transform: translateY(-5px);

          box-shadow:
            0 20px 50px rgba(15, 23, 42, 0.08);
        }

        .cs-workflow-number {
          color: #2563eb;

          font-size: 12px;

          font-weight: 800;

          letter-spacing: 0.08em;

          margin-bottom: 14px;
        }

        .cs-workflow-card h3 {
          margin: 0 0 8px;

          font-size: 17px;

          color: #0f172a;
        }

        .cs-workflow-card p {
          margin: 0;

          color: #64748b;

          font-size: 14px;

          line-height: 1.7;
        }

        /* =========================================================
           TELEMETRY
        ========================================================= */

        .cs-telemetry-grid {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 18px;

          padding-top: 15px;
        }

        .cs-telemetry-card {
          padding: 24px;

          border: 1px solid #e2e8f0;

          border-radius: 18px;

          background: #ffffff;

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-telemetry-card:hover {
          transform: translateY(-5px);

          box-shadow:
            0 20px 45px rgba(15, 23, 42, 0.08);
        }

        .cs-telemetry-icon {
          width: 42px;
          height: 42px;

          border-radius: 12px;

          background:
            linear-gradient(
              135deg,
              rgba(37, 99, 235, 0.12),
              rgba(79, 70, 229, 0.08)
            );

          color: #2563eb;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 13px;

          font-weight: 800;

          margin-bottom: 16px;
        }

        .cs-telemetry-card h3 {
          margin: 0 0 7px;

          color: #0f172a;

          font-size: 16px;
        }

        .cs-telemetry-card p {
          margin: 0;

          color: #64748b;

          font-size: 14px;

          line-height: 1.6;
        }

        /* =========================================================
           FEATURES
        ========================================================= */

        .cs-grid {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 24px;

          padding-top: 15px;
        }

        .cs-feature-card {
          position: relative;

          border: 1px solid #e2e8f0;

          border-radius: 20px;

          background: #ffffff;

          padding: 28px;

          overflow: hidden;

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        }

        .cs-feature-card::before {
          content: "";

          position: absolute;

          width: 120px;
          height: 120px;

          right: -70px;
          top: -70px;

          border-radius: 50%;

          background:
            radial-gradient(
              circle,
              rgba(37, 99, 235, 0.10),
              transparent 70%
            );
        }

        .cs-feature-card:hover {
          transform: translateY(-6px);

          border-color: #dbeafe;

          box-shadow:
            0 25px 60px rgba(15, 23, 42, 0.10);
        }

        .cs-feature-icon {
          width: 42px;
          height: 42px;

          border-radius: 12px;

          background:
            linear-gradient(
              135deg,
              rgba(37, 99, 235, 0.12),
              rgba(79, 70, 229, 0.08)
            );

          color: #2563eb;

          display: flex;

          align-items: center;

          justify-content: center;

          font-weight: 800;

          font-size: 13px;

          margin-bottom: 18px;
        }

        .cs-feature-card h3 {
          margin: 0 0 8px;

          font-size: 16px;

          letter-spacing: -0.02em;

          color: #0f172a;
        }

        .cs-feature-card p {
          margin: 0;

          color: #64748b;

          font-size: 14px;

          line-height: 1.7;
        }

        /* =========================================================
           TECHNOLOGY
        ========================================================= */

        .cs-tech-row {
          display: flex;

          flex-wrap: wrap;

          gap: 12px;

          padding-top: 15px;
        }

        .cs-tech-badge {
          border: 1px solid #e2e8f0;

          background: #ffffff;

          border-radius: 999px;

          padding: 10px 20px;

          font-size: 14px;

          font-weight: 600;

          color: #334155;

          box-shadow:
            0 8px 20px rgba(15, 23, 42, 0.04);

          transition:
            transform 0.2s ease,
            border-color 0.2s ease;
        }

        .cs-tech-badge:hover {
          transform: translateY(-2px);

          border-color: #bfdbfe;
        }

        /* =========================================================
           EMBEDDED SYSTEM
        ========================================================= */

        .cs-component-list {
          display: grid;

          grid-template-columns: repeat(2, 1fr);

          gap: 14px;

          margin-top: 20px;
        }

        .cs-component-item {
          padding: 16px 18px;

          border: 1px solid #e2e8f0;

          border-radius: 14px;

          background: #ffffff;

          color: #334155;

          font-size: 14px;

          font-weight: 600;
        }

        .cs-component-item strong {
          color: #2563eb;
        }

        /* =========================================================
           IMPACT
        ========================================================= */

        .cs-impact-grid {
          display: grid;

          grid-template-columns: repeat(2, 1fr);

          gap: 16px;

          padding-top: 15px;
        }

        .cs-impact-item {
          display: flex;

          align-items: flex-start;

          gap: 12px;

          border: 1px solid #e2e8f0;

          border-radius: 18px;

          padding: 20px 22px;

          background: #ffffff;

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .cs-impact-item:hover {
          transform: translateY(-3px);

          box-shadow:
            0 15px 35px rgba(15, 23, 42, 0.06);
        }

        .cs-impact-check {
          flex-shrink: 0;

          width: 22px;
          height: 22px;

          border-radius: 50%;

          background:
            rgba(37, 99, 235, 0.1);

          color: #2563eb;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 13px;

          font-weight: 800;

          margin-top: 2px;
        }

        .cs-impact-item span:last-child {
          color: #334155;

          font-size: 15px;

          line-height: 1.6;
        }

        /* =========================================================
           SCREENSHOT GALLERY
        ========================================================= */

        .cs-screenshot-grid {
          display: grid;

          grid-template-columns: repeat(3, 1fr);

          gap: 24px;

          margin-top: 15px;
        }

        .cs-screenshot-card {
          overflow: hidden;

          border: 1px solid #e2e8f0;

          border-radius: 20px;

          background: #ffffff;

          box-shadow:
            0 12px 35px rgba(15, 23, 42, 0.05);

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        }

        .cs-screenshot-card:hover {
          transform: translateY(-6px);

          border-color: #dbeafe;

          box-shadow:
            0 25px 60px rgba(15, 23, 42, 0.10);
        }

        .cs-screenshot-preview {
          width: 100%;

          height: 230px;

          display: flex;

          align-items: center;

          justify-content: center;

          overflow: hidden;

          background:
            linear-gradient(
              145deg,
              #f8fafc,
              #ffffff
            );

          border-bottom: 1px solid #e2e8f0;
        }

        .cs-screenshot-preview img {
          width: 100%;

          height: 100%;

          object-fit: contain;

          display: block;

          transition:
            transform 0.4s ease;
        }

        .cs-screenshot-card:hover
        .cs-screenshot-preview img {
          transform: scale(1.03);
        }

        .cs-screenshot-info {
          padding: 18px 20px 20px;
        }

        .cs-screenshot-info h3 {
          margin: 0 0 6px;

          color: #0f172a;

          font-size: 15px;

          font-weight: 700;

          letter-spacing: -0.02em;
        }

        .cs-screenshot-info span {
          color: #94a3b8;

          font-size: 12px;
        }

        /* =========================================================
           CTA
        ========================================================= */

        .cs-cta {
          position: relative;

          overflow: hidden;

          margin-top: 110px;

          padding: 70px 40px;

          border-radius: 30px;

          background:
            radial-gradient(
              circle at 10% 20%,
              rgba(96, 165, 250, 0.18),
              transparent 30%
            ),
            radial-gradient(
              circle at 90% 80%,
              rgba(129, 140, 248, 0.18),
              transparent 30%
            ),
            #0f172a;

          color: white;

          text-align: center;
        }

        .cs-cta::before {
          content: "";

          position: absolute;

          inset: 0;

          border: 1px solid rgba(255, 255, 255, 0.08);

          border-radius: inherit;

          pointer-events: none;
        }

        .cs-cta-content {
          position: relative;

          max-width: 760px;

          margin: 0 auto;
        }

        .cs-cta h2 {
          margin: 0 0 16px;

          font-size: clamp(32px, 4vw, 48px);

          letter-spacing: -0.04em;

          line-height: 1.1;
        }

        .cs-cta p {
          margin: 0 auto 30px;

          max-width: 680px;

          color: #cbd5e1;

          line-height: 1.8;

          font-size: 16px;
        }

        .cs-cta-button {
          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 10px;

          padding: 14px 24px;

          border-radius: 12px;

          background: white;

          color: #0f172a;

          text-decoration: none;

          font-weight: 700;

          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .cs-cta-button:hover {
          transform: translateY(-2px);

          box-shadow:
            0 12px 30px rgba(0, 0, 0, 0.25);
        }

        /* =========================================================
           FOOTER
        ========================================================= */

        .cs-footer-credit {
          text-align: center;

          color: #94a3b8;

          font-size: 13px;

          margin-top: 40px;
        }

        /* =========================================================
           RESPONSIVE
        ========================================================= */

        @media (max-width: 900px) {

          .cs-container {
            padding-top: 50px;
          }

          .cs-project-info {
            grid-template-columns: 1fr;
          }

          .cs-split {
            grid-template-columns: 1fr;
          }

          .cs-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-workflow {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-telemetry-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-credibility {
            grid-template-columns: 1fr;
          }

          .cs-impact-grid {
            grid-template-columns: 1fr;
          }

          .cs-screenshot-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 700px) {

          .cs-container {
            padding-left: 18px;
            padding-right: 18px;
          }

          .cs-hero h1 {
            font-size: 38px;
          }

          .cs-hero-description {
            font-size: 16px;
          }

          .cs-logo {
            width: 135px;
            height: 80px;
          }

          .cs-grid {
            grid-template-columns: 1fr;
          }

          .cs-workflow {
            grid-template-columns: 1fr;
          }

          .cs-telemetry-grid {
            grid-template-columns: 1fr;
          }

          .cs-screenshot-grid {
            grid-template-columns: 1fr;
          }

          .cs-component-list {
            grid-template-columns: 1fr;
          }

          .cs-panel {
            padding: 26px;
          }

          .cs-architecture {
            padding: 25px 18px;
          }

          .cs-architecture-arrow {
            transform: rotate(90deg);
          }

          .cs-cta {
            padding: 50px 22px;

            border-radius: 22px;
          }

          .cs-trust {
            gap: 12px;

            flex-direction: column;
          }

          .cs-official-links {
            flex-direction: column;
            align-items: stretch;
          }

          .cs-official-link {
            justify-content: center;
          }
        }

      `}</style>

      {/* =========================================================
          PAGE
      ========================================================= */}

      <main className="cs-page">

        <div className="cs-container">

          {/* =====================================================
              BREADCRUMB
          ===================================================== */}

          <a
            href="/case-studies"
            className="cs-breadcrumb"
            aria-label="Back to HexxaTech case studies"
          >
            ← Back to Case Studies
          </a>

          {/* =====================================================
              HERO
          ===================================================== */}

          <section className="cs-hero">

            <div className="cs-eyebrow">
              <span className="cs-eyebrow-dot" />
              Space Technology & IoT Case Study
            </div>

            <div className="cs-logo">
              <img
                src={IsetLogo}
                alt="Indo Science Education Trust logo"
              />
            </div>

            <h1>
              Balloon <span className="cs-gradient-text">Satellite</span>
            </h1>

            <p className="cs-subtitle">
              Real-Time Satellite Telemetry & Monitoring System
            </p>

            <p className="cs-hero-description">
              A custom balloon satellite telemetry system developed
              for Indo Science Education Trust, combining embedded
              electronics, environmental sensors, GPS, LoRa
              communication, APIs and a real-time dashboard to monitor
              live flight and environmental data.
            </p>

            <div className="cs-trust">

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Balloon Satellite
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                LoRa Telemetry
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Real-time Dashboard
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Custom PCB
              </div>

            </div>

            <div className="cs-official-links">

              <a
                href="https://indoscience.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="cs-official-link"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "indoscience.org",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                Indo Science Education Trust ↗
              </a>

              <a
                href="https://www.isro.gov.in/spacetutor.html"
                target="_blank"
                rel="noopener noreferrer"
                className="cs-official-link"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "isro.gov.in",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                ISRO Space Tutor Programme ↗
              </a>

            </div>

          </section>

          {/* =====================================================
              PROJECT INFORMATION
          ===================================================== */}

          <section className="cs-project-info">

            <div className="cs-project-info-card">

              <div className="cs-project-info-label">
                Client
              </div>

              <div className="cs-project-info-value">
                Indo Science Education Trust (ISET)
              </div>

            </div>

            <div className="cs-project-info-card">

              <div className="cs-project-info-label">
                Project
              </div>

              <div className="cs-project-info-value">
                Balloon Satellite & Real-Time Telemetry
              </div>

            </div>

            <div className="cs-project-info-card">

              <div className="cs-project-info-label">
                Domain
              </div>

              <div className="cs-project-info-value">
                Space Technology / IoT / Embedded Systems
              </div>

            </div>

          </section>

          {/* =====================================================
              CREDIBILITY
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Recognition & Credentials
            </div>

            <h2>
              A project built around practical space technology.
            </h2>

          </section>

          <section className="cs-credibility">

            <div className="cs-credibility-card">

              <div className="cs-credibility-icon">
                ISRO
              </div>

              <h3>
                ISRO Space Tutor
              </h3>

              <p>
                Indo Science Education Trust is listed by the Indian
                Space Research Organisation (ISRO) among its registered
                Space Tutors in Maharashtra.
              </p>

              <a
                href="https://www.isro.gov.in/spacetutor.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "isro.gov.in",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                Verify on ISRO ↗
              </a>

            </div>

            <div className="cs-credibility-card">

              <div className="cs-credibility-icon">
                ISET
              </div>

              <h3>
                Space & Satellite Education
              </h3>

              <p>
                ISET's official website highlights hands-on space
                technology initiatives including satellite building,
                satellite communication, rocketry and related STEM
                programmes.
              </p>

              <a
                href="https://indoscience.org/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "indoscience.org",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                Visit ISET ↗
              </a>

            </div>

            <div className="cs-credibility-card">

              <div className="cs-credibility-icon">
                REC
              </div>

              <h3>
                Project Recognition
              </h3>

              <p>
                Project documentation includes recognition and
                participation records associated with Indo Science
                Education Trust. Supporting certificates and medals
                are displayed in the project gallery below.
              </p>

            </div>

          </section>

          {/* =====================================================
              PROJECT BACKGROUND
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Project Background
            </div>

            <h2>
              Bringing practical space technology to education.
            </h2>

          </section>

          <section className="cs-split">

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#2563eb" }}
              >
                Indo Science Education Trust
              </div>

              <p>
                Indo Science Education Trust is a science and
                technology education organization focused on bridging
                theoretical and practical STEM knowledge. Its official
                website highlights programmes involving space
                technology, satellite communications, satellite
                building and hands-on engineering activities.
              </p>

              <p
                style={{
                  marginTop: "18px",
                }}
              >
                ISET is also listed by ISRO as a registered Space Tutor,
                supporting its work in space science and technology
                education.
              </p>

            </div>

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#7c3aed" }}
              >
                The Project
              </div>

              <p>
                The project focused on building a balloon satellite
                capable of collecting data from multiple onboard
                sensors and transmitting that information for
                real-time monitoring.
              </p>

              <p
                style={{
                  marginTop: "18px",
                }}
              >
                HexxaTech contributed to the embedded electronics,
                telemetry communication, API integration and software
                dashboard required to transform raw sensor data into
                meaningful live information.
              </p>

            </div>

          </section>

          {/* =====================================================
              SYSTEM ARCHITECTURE
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              System Architecture
            </div>

            <h2>
              From sensors in the sky to a real-time dashboard.
            </h2>

          </section>

          <section className="cs-architecture">

            <div className="cs-architecture-flow">

              <div className="cs-architecture-node">
                Sensors
              </div>

              <div className="cs-architecture-arrow">
                →
              </div>

              <div className="cs-architecture-node">
                ESP32
              </div>

              <div className="cs-architecture-arrow">
                →
              </div>

              <div className="cs-architecture-node">
                LoRa
              </div>

              <div className="cs-architecture-arrow">
                →
              </div>

              <div className="cs-architecture-node">
                Data Receiver
              </div>

              <div className="cs-architecture-arrow">
                →
              </div>

              <div className="cs-architecture-node">
                API
              </div>

              <div className="cs-architecture-arrow">
                →
              </div>

              <div className="cs-architecture-node">
                Live Dashboard
              </div>

            </div>

            <p className="cs-architecture-description">
              Sensor readings are collected onboard the balloon
              satellite, processed through ESP32 and transmitted using
              LoRa. The received telemetry is passed through APIs to
              the monitoring platform, where the information is
              presented through a real-time dashboard.
            </p>

          </section>

          {/* =====================================================
              DATA FLOW
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Data Flow
            </div>

            <h2>
              A complete telemetry pipeline.
            </h2>

          </section>

          <section className="cs-workflow">

            {workflow.map((item) => (

              <div
                className="cs-workflow-card"
                key={item.number}
              >

                <div className="cs-workflow-number">
                  {item.number}
                </div>

                <h3>
                  {item.title}
                </h3>

                <p>
                  {item.text}
                </p>

              </div>

            ))}

          </section>

          {/* =====================================================
              LIVE TELEMETRY
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Live Telemetry
            </div>

            <h2>
              Data monitored in real time.
            </h2>

          </section>

          <section className="cs-telemetry-grid">

            {telemetry.map((item, index) => (

              <div
                className="cs-telemetry-card"
                key={item.title}
              >

                <div className="cs-telemetry-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>
                  {item.title}
                </h3>

                <p>
                  {item.text}
                </p>

              </div>

            ))}

          </section>

          {/* =====================================================
              KEY FEATURES
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Key Features
            </div>

            <h2>
              Hardware, communication and software working together.
            </h2>

          </section>

          <section className="cs-grid">

            {features.map((feature, index) => (

              <div
                className="cs-feature-card"
                key={feature.title}
              >

                <div className="cs-feature-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>
                  {feature.title}
                </h3>

                <p>
                  {feature.text}
                </p>

              </div>

            ))}

          </section>

          {/* =====================================================
              TECHNOLOGY
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Technology
            </div>

            <h2>
              Hardware and software stack.
            </h2>

          </section>

          <section className="cs-tech-row">

            {technologies.map((technology) => (

              <span
                className="cs-tech-badge"
                key={technology}
              >
                {technology}
              </span>

            ))}

          </section>

          {/* =====================================================
              EMBEDDED SYSTEM
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Embedded System
            </div>

            <h2>
              A custom electronics platform for the mission.
            </h2>

          </section>

          <section className="cs-split">

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#2563eb" }}
              >
                Sensor Layer
              </div>

              <p>
                The balloon satellite combines multiple sensors and
                modules including DHT22 for temperature and humidity,
                BMP for atmospheric pressure and NEO-6M for GPS-based
                location and movement information.
              </p>

              <div className="cs-component-list">

                <div className="cs-component-item">
                  <strong>DHT22</strong> — Temperature & Humidity
                </div>

                <div className="cs-component-item">
                  <strong>BMP</strong> — Atmospheric Pressure
                </div>

                <div className="cs-component-item">
                  <strong>NEO-6M</strong> — GPS Location
                </div>

                <div className="cs-component-item">
                  <strong>UV</strong> — Environmental Telemetry
                </div>

              </div>

            </div>

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#7c3aed" }}
              >
                Control & Communication
              </div>

              <p>
                ESP32 acts as the central processing controller while
                LoRa provides long-range telemetry communication. A
                custom-designed PCB brings the electronics together
                into a dedicated hardware platform for the balloon
                satellite.
              </p>

              <div className="cs-component-list">

                <div className="cs-component-item">
                  <strong>ESP32</strong> — Main Controller
                </div>

                <div className="cs-component-item">
                  <strong>LoRa</strong> — Long-range Telemetry
                </div>

                <div className="cs-component-item">
                  <strong>Custom PCB</strong> — Hardware Integration
                </div>

                <div className="cs-component-item">
                  <strong>API</strong> — Dashboard Integration
                </div>

              </div>

            </div>

          </section>

          {/* =====================================================
              PROJECT IMPACT
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Project Impact
            </div>

            <h2>
              Turning satellite telemetry into usable information.
            </h2>

          </section>

          <section className="cs-impact-grid">

            {impact.map((item) => (

              <div
                className="cs-impact-item"
                key={item}
              >

                <span className="cs-impact-check">
                  ✓
                </span>

                <span>
                  {item}
                </span>

              </div>

            ))}

          </section>

          {/* =====================================================
              PROJECT GALLERY
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Project Gallery
            </div>

            <h2>
              Project records, certificates and documentation.
            </h2>

          </section>

          <section className="cs-screenshot-grid">

            {screenshotData.map((item) => {

              const title =
                screenshotTitles[item.fileName] ||
                item.fileName.replace(/\.[^/.]+$/, "");

              return (
                <div
                  className="cs-screenshot-card"
                  key={item.fileName}
                >

                  <div className="cs-screenshot-preview">

                    <img
                      src={item.image}
                      alt={`${title} - Indo Science Education Trust balloon satellite project`}
                      className="cs-screenshot-image"
                      loading="lazy"
                    />

                  </div>

                  <div className="cs-screenshot-info">

                    <h3>
                      {title}
                    </h3>

                    <span>
                      Indo Science Education Trust
                    </span>

                  </div>

                </div>
              );
            })}

          </section>

          {/* =====================================================
              CREDIBILITY NOTE
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Project Credentials
            </div>

            <h2>
              Documented recognition and official references.
            </h2>

          </section>

          <section className="cs-panel">

            <div
              className="cs-panel-label"
              style={{ color: "#2563eb" }}
            >
              Official Verification
            </div>

            <p>
              Indo Science Education Trust is listed in ISRO's official
              Space Tutor programme as a registered Space Tutor in
              Maharashtra. ISET's own website also highlights its work
              in satellite and space technology education, including
              hands-on satellite building and related programmes.
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                marginTop: "22px",
              }}
            >

              <a
                href="https://www.isro.gov.in/spacetutor.html"
                target="_blank"
                rel="noopener noreferrer"
                className="cs-official-link"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "isro.gov.in",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                Official ISRO Reference ↗
              </a>

              <a
                href="https://indoscience.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="cs-official-link"
                onClick={() =>
                  trackEvent("outbound_link_click", {
                    destination: "indoscience.org",
                    case_study: "ISET Balloon Satellite",
                  })
                }
              >
                Official ISET Website ↗
              </a>

            </div>

          </section>

          {/* =====================================================
              RELATED SERVICES
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Related Services
            </div>

            <h2>
              Services that complement this technology project.
            </h2>

          </section>

          <section className="cs-feature-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "42px" }}>
            <article className="cs-panel" style={{ padding: "22px" }}>
              <h3 style={{ marginTop: 0 }}>Custom Software Development</h3>
              <p>Build software that turns scientific, operational and monitoring workflows into usable digital systems.</p>
              <Link to="/services/custom-software-development" style={{ color: "#2563eb", fontWeight: 700 }}>Explore Custom Software Development →</Link>
            </article>
            <article className="cs-panel" style={{ padding: "22px" }}>
              <h3 style={{ marginTop: 0 }}>Mobile App Development</h3>
              <p>Deliver field and monitoring-related mobile experiences that connect with your underlying systems.</p>
              <Link to="/services/mobile-app-development" style={{ color: "#2563eb", fontWeight: 700 }}>Explore Mobile App Development →</Link>
            </article>
          </section>

          {/* =====================================================
              CTA
          ===================================================== */}

          <section className="cs-cta">

            <div className="cs-cta-content">

              <h2>
                Have an idea that connects
                <br />
                hardware and software?
              </h2>

              <p>
                From embedded systems and IoT devices to real-time
                telemetry dashboards and custom software platforms,
                HexxaTech builds technology solutions that connect
                physical data with meaningful digital experiences.
              </p>

                <a
                  href="https://api.whatsapp.com/send/?phone=919226591664&text=Hello+HexxaTech%2C+I+would+like+to+discuss+a+software+project.&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                  onClick={() => trackWhatsAppClick("iset_case_study_cta")}
                >
                  Discuss Your Project
                  <span aria-hidden="true">→</span>
                </a>


            </div>

          </section>

          <p className="cs-footer-credit">
            Developed by HexxaTech Softwares
          </p>

        </div>

      </main>
    </>
  );
}
